import React from "react";
import {
  Box,
  Typography,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export default function PipeBuilderResults({
  value,
  result,
  onExecuteQuery,
  isExpanded,
  setIsExpanded,
  isLoading,
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  console.log("PipeBuilderResults - value:", value);
  console.log("PipeBuilderResults - result:", result);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderExecuteButton = () => (
    <Box sx={{ mb: 2 }}>
      <Button
        variant="contained"
        color="primary"
        onClick={onExecuteQuery}
        disabled={isLoading || !value || value.length === 0}
        startIcon={isLoading ? <CircularProgress size={20} /> : null}
      >
        {isLoading
          ? "Loading Enhanced Results..."
          : "Execute Query with Join Data"}
      </Button>

      {value && value.length > 0 && (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Query has {value.length} condition(s) - this will show matching leads
          with related data from campaigns, visits, and engagements. It filters
          through the 100 newest leads- these are NOT necessarily what will be
          filtered when the query is run as part of day to day operations
        </Typography>
      )}
    </Box>
  );

  const renderEnhancedResults = (data) => {
    if (!data || data.length === 0) {
      return (
        <Alert severity="info">
          No matching leads found for your query conditions.
        </Alert>
      );
    }

    // Get column headers dynamically from the first result
    const firstRow = data[0];
    const columns = Object.keys(firstRow);

    // Paginate data
    const paginatedData = data.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Enhanced Query Results
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Found{" "}
          <Box component="span" sx={{ color: "white", fontWeight: "bold" }}>
            {data.length}
          </Box>{" "}
          matching lead(s) with join variables from related tables.
        </Typography>

        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column} sx={{ fontWeight: "bold" }}>
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <TableRow key={index}>
                  {columns.map((column) => (
                    <TableCell key={column}>
                      {formatCellValue(column, row[column])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {data.length > rowsPerPage && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Box>
    );
  };

  const formatCellValue = (column, value) => {
    // Handle null/undefined values
    if (value === null || value === undefined) {
      return <Chip label="null" size="small" variant="outlined" />;
    }

    // Format boolean values
    if (typeof value === "boolean") {
      return (
        <Chip
          label={value ? "Yes" : "No"}
          size="small"
          color={value ? "success" : "default"}
        />
      );
    }

    // Format dates
    if (
      column.toLowerCase().includes("date") ||
      column.toLowerCase().includes("time")
    ) {
      const date = new Date(value);
      if (!isNaN(date)) {
        return date.toLocaleString();
      }
    }

    // Handle numeric IDs
    if (column.toLowerCase().includes("id") && typeof value === "number") {
      return <Chip label={`ID: ${value}`} size="small" variant="outlined" />;
    }

    // Format campaign names
    if (
      column.toLowerCase().includes("campaign") &&
      column.toLowerCase().includes("name") &&
      value
    ) {
      return <Chip label={value} size="small" color="primary" />;
    }

    // Truncate long text
    if (typeof value === "string" && value.length > 50) {
      return (
        <Typography variant="body2" title={value}>
          {value.substring(0, 50)}...
        </Typography>
      );
    }

    return value;
  };

  const renderResult = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 3,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading enhanced results with join data...
          </Typography>
        </Box>
      );
    }

    if (!result) {
      return (
        <Alert severity="info">
          No query result available. Build a query in the first tab and click
          "Execute Query with Join Data" to see enhanced results.
        </Alert>
      );
    }

    if (result.error) {
      return <Alert severity="error">Error: {result.error}</Alert>;
    }

    // Handle the new enhanced API response format
    if (result.QueryType === "enhanced" && Array.isArray(result.Data)) {
      return renderEnhancedResults(result.Data);
    }

    // Handle legacy formats (backward compatibility)
    // If result is a string (SQL query)
    if (typeof result === "string") {
      return (
        <Box sx={{ maxWidth: "400px" }}>
          <Typography variant="h6" gutterBottom>
            Generated SQL Query:
          </Typography>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "grey.100",
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              overflow: "auto",
            }}
          >
            <Typography sx={{ color: "black" }}>{result}</Typography>
          </Paper>
        </Box>
      );
    }

    // If result is an array of lead IDs (numbers) - legacy support
    if (Array.isArray(result)) {
      const isLeadIds = result.every(
        (item) => typeof item === "number" || !isNaN(Number(item))
      );

      if (isLeadIds && result.length > 0) {
        const leadIds = result.map((id) => Number(id));

        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Matching Lead IDs
            </Typography>
            <Paper sx={{ p: 2, backgroundColor: "grey.50" }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Found {leadIds.length} matching lead(s):
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {leadIds.slice(0, 20).map((id) => (
                  <Chip key={id} label={`Lead ${id}`} size="small" />
                ))}
                {leadIds.length > 20 && (
                  <Chip
                    label={`+${leadIds.length - 20} more`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </Paper>
          </Box>
        );
      }
    }

    // Fallback for other result types
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Query Result:
        </Typography>
        <Paper
          sx={{
            p: 2,
            backgroundColor: "grey.50",
            maxHeight: "500px",
            overflow: "auto",
          }}
        >
          <pre style={{ margin: 0, fontSize: "0.875rem" }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        </Paper>
        {Array.isArray(result) && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Found {result.length} result(s)
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="textSecondary">
          This enhanced preview runs the query and shows not only matching
          leads, but also the related data from campaigns, visits, and
          engagements that were used in your filtering conditions.
        </Typography>
      </Box>

      {/* Controlled Accordion */}
      <Accordion
        expanded={isExpanded}
        onChange={(event, expanded) => setIsExpanded(expanded)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Query Results</Typography>
        </AccordionSummary>
        <AccordionDetails>{renderResult()}</AccordionDetails>
      </Accordion>
    </Box>
  );
}
